import { LucideIcon } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function TabNavigation({ tabs, activeTab, onTabChange }: TabNavigationProps) {
  return (
    <nav className="lg:space-y-1">
      {/* Mobile horizontal tabs */}
      <div className="lg:hidden relative overflow-hidden">
        <div className="flex overflow-x-auto gap-2 pb-2 pl-4 pr-8 -ml-4 scrollbar-hide">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 whitespace-nowrap flex-shrink-0
                ${index === tabs.length - 1 ? 'mr-4' : ''}
                ${
                  activeTab === tab.id
                    ? 'bg-gray-100 dark:bg-gray-700 text-[#E34290] dark:text-[#E34290]'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-[#E34290] dark:hover:text-[#E34290]'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Left fade gradient - only show when not at start */}
        <div className="absolute left-0 top-0 bottom-2 w-6 bg-gradient-to-r from-white dark:from-gray-900 via-white/80 dark:via-gray-900/80 to-transparent pointer-events-none opacity-0 transition-opacity duration-200"></div>

        {/* Right fade gradient with scroll indicator */}
        <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white dark:from-gray-900 via-white/90 dark:via-gray-900/90 to-transparent pointer-events-none">
          {/* Scroll indicator dots */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex space-x-0.5">
            <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Desktop vertical tabs */}
      <div className="hidden lg:block space-y-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
            w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-150
            ${
              activeTab === tab.id
                ? 'bg-gray-100 dark:bg-gray-700 text-[#E34290] dark:text-[#E34290]'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-[#E34290] dark:hover:text-[#E34290]'
            }
          `}
          >
            <tab.icon className="h-5 w-5" />
            {tab.label}
          </button>
        ))}
      </div>
    </nav>
  );
}
